import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, ServerCrash, Wifi, WifiOff } from "lucide-react";
import { NETWORK_STATUS, NETWORK_STATUS_EVENT } from "../../utils/networkStatusEvents";
import { resolveApiUrl } from "../../utils/apiBaseUrl";

const CHECK_INTERVAL_MS = 15000;

const getInitialStatus = () => (
    typeof navigator !== "undefined" && !navigator.onLine
        ? NETWORK_STATUS.OFFLINE
        : NETWORK_STATUS.ONLINE
);

const NetworkStatusNotification = () => {
    const [status, setStatus] = useState(getInitialStatus);
    const [isChecking, setIsChecking] = useState(false);

    const checkServerStatus = useCallback(async () => {
        if (!navigator.onLine) {
            setStatus(NETWORK_STATUS.OFFLINE);
            return;
        }

        setIsChecking(true);

        try {
            const response = await fetch(resolveApiUrl("/api/health"), {
                cache: "no-store",
                credentials: "include",
            });

            setStatus(response.ok ? NETWORK_STATUS.ONLINE : NETWORK_STATUS.SERVER_DOWN);
        } catch {
            setStatus(NETWORK_STATUS.SERVER_DOWN);
        } finally {
            setIsChecking(false);
        }
    }, []);

    useEffect(() => {
        const handleOffline = () => setStatus(NETWORK_STATUS.OFFLINE);
        const handleOnline = () => {
            setStatus(NETWORK_STATUS.SERVER_DOWN);
            checkServerStatus();
        };
        const handleNetworkStatus = (event) => {
            if (event.detail?.status) {
                setStatus(event.detail.status);
            }
        };

        window.addEventListener("offline", handleOffline);
        window.addEventListener("online", handleOnline);
        window.addEventListener(NETWORK_STATUS_EVENT, handleNetworkStatus);

        if (navigator.onLine) {
            checkServerStatus();
        }

        return () => {
            window.removeEventListener("offline", handleOffline);
            window.removeEventListener("online", handleOnline);
            window.removeEventListener(NETWORK_STATUS_EVENT, handleNetworkStatus);
        };
    }, [checkServerStatus]);

    useEffect(() => {
        if (status === NETWORK_STATUS.ONLINE) return undefined;

        const intervalId = window.setInterval(checkServerStatus, CHECK_INTERVAL_MS);
        return () => window.clearInterval(intervalId);
    }, [checkServerStatus, status]);

    const content = useMemo(() => {
        if (status === NETWORK_STATUS.OFFLINE) {
            return {
                Icon: WifiOff,
                title: "You're offline",
                message: "Check your internet connection. Changes may not sync until you're back online.",
                className: "network-status-notification--offline",
            };
        }

        if (status === NETWORK_STATUS.SERVER_DOWN) {
            return {
                Icon: ServerCrash,
                title: "Server unavailable",
                message: "The app cannot reach the server right now. We'll keep checking in the background.",
                className: "network-status-notification--server",
            };
        }

        return null;
    }, [status]);

    if (!content) return null;

    const { Icon, title, message, className } = content;

    return (
        <div className={`network-status-notification ${className}`} role="status" aria-live="polite">
            <div className="network-status-notification__icon">
                <Icon size={20} aria-hidden="true" />
            </div>
            <div className="network-status-notification__content">
                <strong>{title}</strong>
                <span>{message}</span>
            </div>
            <button
                type="button"
                className="network-status-notification__button"
                onClick={checkServerStatus}
                disabled={isChecking}
                title="Check connection"
                aria-label="Check connection"
            >
                {isChecking ? <RefreshCw size={18} className="network-status-notification__spinner" /> : <Wifi size={18} />}
            </button>
        </div>
    );
};

export default NetworkStatusNotification;
